/**
 * Support at Home funding levels and their annual budgets — the ONE table every screen, the
 * contribution engine and the advice documents read.
 *
 * SOURCE: Department of Health, Disability and Ageing, "Support at Home program – classifications
 * and budgets" (edition effective 1 July 2026; filed in docs/reference) and the Department's
 * "Funding classifications for Support at Home" page. Budgets index on 1 July each year. Until
 * 17 Sep 2026 this table carried the 1 November 2025 figures (actuarial review pack F.33).
 *
 * TWO KINDS OF LEVEL. A person assessed on or after 1 November 2025 receives one of the EIGHT
 * classifications on their Notice of Decision. A person who was receiving a Home Care Package, or
 * approved for one, before 1 November 2025 was moved onto a TRANSITIONED HCP LEVEL with the same
 * funding as their package (levels 1–4) — including people who were still on the waiting list, who
 * receive that funding when a place becomes available. They stay on it until they are reassessed
 * into a classification. The transitioned levels are marked so the "eight classifications" tables
 * on the Support at Home pages do not list them as classifications.
 */
export interface AcatLevelInfo {
    label: string;
    budget: number;
    description: string;
    /** A transitioned Home Care Package level (1–4), not one of the eight classifications. */
    transitional?: boolean;
}

export const ACAT_LEVEL_DATA: Record<string, AcatLevelInfo> = {
    sah_level_1: {
        label: 'Support at Home - Classification 1',
        budget: 11010.01,
        description: 'Basic support for low care needs. Typical services include housekeeping, social support, and personal care assistance.',
    },
    sah_level_2: {
        label: 'Support at Home - Classification 2',
        budget: 16451.35,
        description: 'Low-level care needs. Supports activities of daily living, personal care, and occasional allied health services.',
    },
    sah_level_3: {
        label: 'Support at Home - Classification 3',
        budget: 22536.81,
        description: 'Intermediate care needs. Regular personal care, domestic assistance, allied health, and social support services.',
    },
    sah_level_4: {
        label: 'Support at Home - Classification 4',
        budget: 30468.51,
        description: 'Intermediate-high care needs. Increased personal care, nursing support, allied health, and community access services.',
    },
    sah_level_5: {
        label: 'Support at Home - Classification 5',
        budget: 40729.53,
        description: 'High care needs. Significant personal care, nursing, allied health, and more frequent service delivery.',
    },
    sah_level_6: {
        label: 'Support at Home - Classification 6',
        budget: 49365.27,
        description: 'High care needs. Comprehensive care package with extensive personal care, nursing, allied health, and clinical support.',
    },
    sah_level_7: {
        label: 'Support at Home - Classification 7',
        budget: 59660.0,
        description: 'Very high care needs. Intensive personal care, frequent nursing visits, extensive allied health, and palliative care support.',
    },
    sah_level_8: {
        label: 'Support at Home - Classification 8',
        budget: 80137.12,
        description: 'Highest level of care. Maximum support with intensive personal care, complex nursing management, specialist allied health, and comprehensive clinical services.',
    },
    hcp_transition_1: {
        label: 'Home Care Package Level 1 (transitioned)',
        budget: 11272.15,
        description: 'The funding of a Level 1 Home Care Package, carried into Support at Home unchanged. Applies until a new assessment places the person in one of the eight classifications.',
        transitional: true,
    },
    hcp_transition_2: {
        label: 'Home Care Package Level 2 (transitioned)',
        budget: 19821.76,
        description: 'The funding of a Level 2 Home Care Package, carried into Support at Home unchanged. Applies until a new assessment places the person in one of the eight classifications.',
        transitional: true,
    },
    hcp_transition_3: {
        label: 'Home Care Package Level 3 (transitioned)',
        budget: 43148.74,
        description: 'The funding of a Level 3 Home Care Package, carried into Support at Home unchanged. Applies until a new assessment places the person in one of the eight classifications.',
        transitional: true,
    },
    hcp_transition_4: {
        label: 'Home Care Package Level 4 (transitioned)',
        budget: 65415.91,
        description: 'The funding of a Level 4 Home Care Package, carried into Support at Home unchanged. Applies until a new assessment places the person in one of the eight classifications.',
        transitional: true,
    },
};

/** The eight classifications, in order — what the Support at Home pages list as classifications. */
export const SAH_CLASSIFICATION_KEYS = Object.keys(ACAT_LEVEL_DATA).filter((k) => !ACAT_LEVEL_DATA[k]!.transitional);
/** The four transitioned Home Care Package levels, in order. */
export const TRANSITIONED_HCP_LEVEL_KEYS = Object.keys(ACAT_LEVEL_DATA).filter((k) => ACAT_LEVEL_DATA[k]!.transitional);

/** Annual budget for a level key; 0 for none or an unknown key. */
export function budgetForLevel(level: string | null | undefined): number {
    return level ? ACAT_LEVEL_DATA[level]?.budget ?? 0 : 0;
}
